import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useStore } from 'zustand';

import { formatDateWithoutHour, isPassed } from 'app/utils/dates';
import { isSunday } from 'app/utils/dayjs';

import { inventoryStore } from '../../InventoryStore';
import { OverviewCell } from '../../_components/Cells/OverviewCell';
import { WatchingCell } from '../../_components/Cells/WatchingCell';
import type { FormPackageBooking } from '../../type';

import './Row.scss';

type OverviewRowProps = {
  dates?: Date[];
  firstColumnContent: string | ReactNode;
  packageId?: number;
  fieldName?: string;
  getDescriptionValue?: (
    datePlanning: FormPackageBooking,
    date: string
  ) => number | string | undefined;
  isEditable: boolean;
  getValue?: (datePlanning: FormPackageBooking, date: string) => number;
  removeBorderBottom?: boolean;
  getErrors?: (date: string) => string[];
};

export const OverviewRow = ({
  dates = [],
  firstColumnContent,
  packageId,
  isEditable,
  fieldName,
  getDescriptionValue,
  getValue,
  removeBorderBottom = false,
  getErrors,
}: OverviewRowProps) => {
  const bookableDays = useStore(inventoryStore, (state) => state.bookableDays);

  const isEditMode = useStore(inventoryStore, (store) => store.isEditMode);

  const selectPackage = useStore(
    inventoryStore,
    (state) => state.selectPackage
  );

  if (packageId !== undefined && !selectPackage.includes(packageId)) {
    return null;
  }

  return (
    <div className="table-line">
      <div
        className={classNames('first-column', {
          'no-border-bottom': removeBorderBottom,
        })}
      >
        {firstColumnContent}
      </div>
      {dates.map((date) => {
        const formattedDate = formatDateWithoutHour(date);
        const basePath = packageId
          ? ['packages', packageId.toString(), 'bookings', formattedDate]
          : ['packages'];
        const nameField = fieldName ? [...basePath, fieldName] : undefined;

        return (
          <WatchingCell
            name={basePath}
            key={`${packageId ? packageId : 'total_stock'}_${formattedDate}`}
          >
            {(values?: FormPackageBooking) => {
              if (!values) {
                return null;
              }

              const closed =
                isPassed(date) || !bookableDays.includes(formattedDate);

              const cellClass =
                getErrors && !closed ? getErrors(formattedDate) : [];

              return (
                <div
                  className={classNames('other-column', ...cellClass, {
                    'bold-border': isSunday(date),
                    closed: closed,
                    'edit-mode': isEditMode,
                  })}
                  key={`overview_package_${packageId}_${formattedDate}_${nameField}`}
                >
                  <OverviewCell
                    date={date}
                    nameField={nameField}
                    name={fieldName}
                    isEditable={isEditable}
                    key={`${packageId}_${formattedDate}_${nameField}`}
                    datePlanning={values}
                    getValue={getValue}
                    getDescriptionValue={getDescriptionValue}
                    packageId={packageId}
                  />
                </div>
              );
            }}
          </WatchingCell>
        );
      })}
    </div>
  );
};
